/**
 * @fileoverview CheckboxGroupRenderer component for the Storyteller web application.
 * Displays a group of checkboxes with options from a JSON schema.
 *
 * @example
 * // Basic usage
 * <CheckboxGroupRenderer schema={schema} uischema={uischema} />
 */

import { ControlProps, JsonSchema4, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsEnumProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../../../config'
import css from './CheckboxGroupRenderer.module.css'

/**
 * @function classNames
 * @description Combines multiple class names into a single string.
 * @param {...string[]} classes - The class names to combine.
 * @returns {string} The combined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function castValue
 * @description Casts a value to the appropriate type based on the schema.
 * @param {T} value - The value to cast.
 * @param {JsonSchema4} schema - The schema to use for casting.
 * @returns {T} The casted value.
 */
function castValue<T>(value: T, schema: JsonSchema4) {
    switch ((schema.items as JsonSchema4)?.type) {
        case 'number':
            return Number(value)
        case 'string':
            return value.toString()
        default:
            throw new Error('Unsupported type')
    }
}

/**
 * @function CheckboxGroup
 * @description Component for rendering a group of checkboxes.
 * @param {ControlProps & VanillaRendererProps} props - The props for the CheckboxGroup component.
 * @returns {JSX.Element} The CheckboxGroup component.
 */
function CheckboxGroup({
    schema,
    uischema,
    errors,
    handleChange,
    path,
    required,
}: ControlProps & VanillaRendererProps) {
    const [state, setState] = useState<unknown[]>([])
    const { t } = useTranslation()
    const hasError = errors.length > 0

    useEffect(() => {
        if (state.length === 0) {
            handleChange(path, undefined)
        } else {
            handleChange(path, state)
        }
    }, [state])

    return (
        <>
            <div
                className={classNames(css.container, hasError && css.error)}
                style={uischema.options?.style}
            >
                {(schema.items as JsonSchema4).oneOf?.map((value) => (
                    <label
                        htmlFor={`${path}-${value.const}`}
                        className={css.item}
                        tabIndex={0}
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                        role="checkbox"
                        aria-checked={state.includes(value.const)}
                        key={`option-${value.const}`}
                        onKeyUp={(e) => {
                            if (e.key === ' ') {
                                const v = castValue(value.const, schema as JsonSchema4)
                                if (state.includes(v)) {
                                    setState(state.filter((v) => v !== value.const))
                                } else {
                                    setState([...state, v])
                                }
                            }
                        }}
                    >
                        <div className={css.checkbox}>
                            <input
                                name={`${path}-${value.const}`}
                                id={`${path}-${value.const}`}
                                type="checkbox"
                                checked={state.includes(value.const)}
                                required={required}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const v = castValue(value.const, schema as JsonSchema4)
                                    if (e.target.checked) {
                                        setState([...state, v])
                                    } else {
                                        setState(state.filter((v) => v !== value.const))
                                    }
                                }}
                            />
                            <label htmlFor={`${path}-${value.const}`}>
                                {state.includes(value.const) ? (
                                    <img
                                        src={config.s3.getStaticURL('checkbox.svg')}
                                        width={24}
                                        height={24}
                                        alt="checkbox"
                                    />
                                ) : (
                                    <div className={css.unchecked} />
                                )}
                            </label>
                        </div>
                        <div className={css.label}>
                            <label className={css.label} htmlFor={`${path}-${value.const}`}>
                                {t(`options.${uischema.options?.name}.${value.title}`)}
                            </label>
                        </div>
                    </label>
                ))}
            </div>
            {hasError && <div className={css.errorMessage}>{errors}</div>}
        </>
    )
}

export const CheckboxGroupTester = rankWith(3, uiTypeIs('CheckboxGroup'))

export default withVanillaControlProps(withJsonFormsEnumProps(CheckboxGroup))
